import { useMemo } from 'react';
import { Input, Select } from 'antd';
import { SearchOutlined } from '@ant-design/icons';
import {
  useGetStudySitesOptionsQuery,
  useGetStudyModalitiesOptionsQuery,
} from '@/state/slices/options';
import type { DatePreset } from '@/models/report';
import type { UploadStatus, UploadStudyType } from '@/models/upload';
import styles from './UploadsFilterBar.module.css';

interface UploadsFilterBarProps {
  searchText: string;
  onSearchChange: (text: string) => void;
  selectedStatuses: UploadStatus[];
  onStatusesChange: (statuses: UploadStatus[]) => void;
  selectedStudyTypes: UploadStudyType[];
  onStudyTypesChange: (types: UploadStudyType[]) => void;
  selectedSites: string[];
  onSitesChange: (sites: string[]) => void;
  datePreset: DatePreset | 'all';
  onDatePresetChange: (preset: DatePreset | 'all') => void;
}

const DATE_OPTIONS: { label: string; value: DatePreset | 'all' }[] = [
  { label: 'All', value: 'all' },
  { label: 'Today', value: 'today' },
  { label: 'Last 7 days', value: 'last7' },
  { label: 'Last 30 days', value: 'last30' },
];

const STATUS_OPTIONS: { label: string; value: UploadStatus }[] = [
  { label: 'Ready', value: 'READY' },
  { label: 'Processing', value: 'PROCESSING' },
  { label: 'Failed', value: 'FAILED' },
];

export default function UploadsFilterBar({
  searchText,
  onSearchChange,
  selectedStatuses,
  onStatusesChange,
  selectedStudyTypes,
  onStudyTypesChange,
  selectedSites,
  onSitesChange,
  datePreset,
  onDatePresetChange,
}: UploadsFilterBarProps) {
  const { data: sites } = useGetStudySitesOptionsQuery();
  const { data: modalities } = useGetStudyModalitiesOptionsQuery();

  const siteOptions = useMemo(
    () => (sites ?? []).map((s) => ({ value: s, label: s })),
    [sites]
  );

  const studyTypeOptions = useMemo(
    () => (modalities ?? []).map((m) => ({ value: m, label: m })),
    [modalities]
  );

  return (
    <div className={styles.bar}>
      <Input
        className={styles.search}
        prefix={<SearchOutlined />}
        placeholder="Search name, MRN, or study"
        value={searchText}
        onChange={(e) => onSearchChange(e.target.value)}
        allowClear
      />

      <Select
        className={styles.select}
        mode="multiple"
        allowClear
        maxTagCount="responsive"
        placeholder="Status: All"
        value={selectedStatuses}
        onChange={(v) => onStatusesChange(v)}
        options={STATUS_OPTIONS}
        aria-label="Status filter"
      />

      <Select
        className={styles.select}
        value={datePreset}
        onChange={onDatePresetChange}
        options={DATE_OPTIONS}
        aria-label="Date filter"
      />

      <Select
        className={styles.select}
        mode="multiple"
        allowClear
        maxTagCount="responsive"
        placeholder="Study type: All"
        value={selectedStudyTypes}
        onChange={(v) => onStudyTypesChange(v)}
        options={studyTypeOptions}
        aria-label="Study type filter"
      />

      <Select
        className={styles.select}
        mode="multiple"
        allowClear
        maxTagCount="responsive"
        placeholder="Site: All"
        value={selectedSites}
        onChange={onSitesChange}
        options={siteOptions}
        aria-label="Site filter"
      />
    </div>
  );
}
